import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchCustomMc, addCustomMc, deleteCustomMc } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useGame } from "../context/GameContext";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Pickaxe, Plus, Trash2 } from "lucide-react";

const inputStyle = {
  background: "var(--bg-main)",
  borderColor: "var(--border-strong)",
  color: "var(--text-primary)",
  borderRadius: 2,
};

const CustomMcPanel = () => {
  const { user, login } = useAuth();
  const { game } = useGame();
  const authed = !!(user && user.user_id);
  const [host, setHost] = useState("");
  const [name, setName] = useState("");
  const [region, setRegion] = useState("EU-Central");
  const qc = useQueryClient();

  const { data: list = [], isLoading } = useQuery({
    queryKey: ["custom-mc"],
    queryFn: fetchCustomMc,
    enabled: authed,
  });

  const addMut = useMutation({
    mutationFn: (payload) => addCustomMc(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["custom-mc"] });
      qc.invalidateQueries({ queryKey: ["servers"] });
      qc.invalidateQueries({ queryKey: ["games"] });
      toast.success("Custom Minecraft server added");
      setHost("");
      setName("");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not add server"),
  });

  const delMut = useMutation({
    mutationFn: (id) => deleteCustomMc(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["custom-mc"] });
      qc.invalidateQueries({ queryKey: ["servers"] });
      toast.success("Removed");
    },
  });

  if (!game || game.source !== "minecraft") return null;

  return (
    <div className="tac-card p-4 rise" data-testid="custom-mc-panel">
      <span className="corner-tr" />
      <span className="corner-bl" />
      <div className="flex items-center gap-2 mb-3">
        <Pickaxe className="w-5 h-5" style={{ color: "var(--primary-accent)" }} />
        <div>
          <div className="font-head uppercase font-bold tracking-wide text-lg" style={{ color: "var(--text-primary)" }}>
            MY MINECRAFT SERVERS
          </div>
          <div className="hud-caption">Add any Minecraft server by host — it joins your personal list</div>
        </div>
      </div>

      {!authed ? (
        <div className="p-4 border" style={{ borderColor: "var(--border-color)", background: "var(--bg-main)" }}>
          <p className="text-sm mb-3" style={{ color: "var(--text-secondary)" }}>
            Login with Google to add custom Minecraft servers to this list.
          </p>
          <Button
            onClick={login}
            data-testid="mc-login-btn"
            className="font-mono-hud tracking-widest"
            style={{ background: "var(--primary-accent)", color: "#000", borderRadius: 2 }}
          >
            LOGIN WITH GOOGLE
          </Button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-[2fr_2fr_1fr_auto] gap-2 mb-3">
            <Input
              data-testid="mc-host-input"
              placeholder="e.g. mc.example.com or 1.2.3.4:25565"
              value={host}
              onChange={(e) => setHost(e.target.value)}
              style={inputStyle}
            />
            <Input
              data-testid="mc-name-input"
              placeholder="Display name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={inputStyle}
            />
            <select
              data-testid="mc-region-input"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className="px-3 h-10 border font-mono-hud text-sm"
              style={inputStyle}
            >
              <option value="EU-Central">EU-Central</option>
              <option value="EU-West">EU-West</option>
              <option value="US-East">US-East</option>
              <option value="US-West">US-West</option>
              <option value="Asia-East">Asia-East</option>
            </select>
            <Button
              onClick={() => addMut.mutate({ host: host.trim(), display_name: name.trim(), region })}
              disabled={!host.trim() || addMut.isPending}
              data-testid="mc-add-btn"
              className="font-mono-hud tracking-widest font-bold"
              style={{ background: "var(--primary-accent)", color: "#000", borderRadius: 2 }}
            >
              <Plus className="w-4 h-4 mr-1" /> ADD
            </Button>
          </div>

          {isLoading ? (
            <div className="hud-caption">LOADING...</div>
          ) : list.length === 0 ? (
            <div className="hud-caption py-3">NO CUSTOM SERVERS YET</div>
          ) : (
            <div className="space-y-2">
              {list.map((entry) => (
                <div
                  key={entry.id}
                  className="flex items-center justify-between gap-3 px-3 py-2 border"
                  style={{ borderColor: "var(--border-color)", background: "var(--bg-main)" }}
                  data-testid={`mc-entry-${entry.id}`}
                >
                  <div className="min-w-0">
                    <div className="font-head font-bold uppercase text-sm truncate" style={{ color: "var(--text-primary)" }}>
                      {entry.display_name}
                    </div>
                    <div className="font-mono-hud text-xs" style={{ color: "var(--text-dim)" }}>
                      {entry.host} · {entry.region}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => delMut.mutate(entry.id)}
                    data-testid={`mc-delete-${entry.id}`}
                    style={{ color: "var(--secondary-accent)" }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CustomMcPanel;
